import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Plan } from "../types";
import { useToast } from "../toast";
import PlanDialog from "../dialogs/PlanDialog";

export default function Plans({ onOpenDetail }: { onOpenDetail?: (id: number) => void }) {
  const api = useAuthApi();
  const [items, setItems] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api<Plan[]>(`${MGMT}/plans`);
      setItems(data ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const chargerLabel = (p: Plan) => {
    if (p.charger_config) return p.charger_config.display_name ?? p.charger_config.charge_point_id;
    if (p.charger_group) return `Group: ${p.charger_group.name}`;
    return "–";
  };

  const eligibilityLabel = (p: Plan) => {
    if (p.driver) return p.driver.name;
    if (p.driver_group) return `Group: ${p.driver_group.name}`;
    return "Open to all";
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const haystack = [
        p.name,
        p.description,
        chargerLabel(p),
        p.tariff?.name,
        eligibilityLabel(p),
        p.is_active ? "active" : "inactive",
        p.requires_code ? "code required" : "no code",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
        >
          New Plan
        </Button>
        <TextField
          size="small"
          placeholder="Filter plans…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {hasFilters && (
          <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Charger / Group</TableCell>
              <TableCell>Tariff</TableCell>
              <TableCell>Eligibility</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters
                        ? "No plans match the current filters."
                        : "No plans yet. Create a plan to let drivers subscribe and generate contracts automatically."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => (
              <TableRow
                key={p.id}
                hover
                sx={{ cursor: onOpenDetail ? "pointer" : undefined }}
                onClick={() => onOpenDetail?.(p.id)}
              >
                <TableCell sx={{ fontWeight: 600 }}>
                  {p.name}
                  {p.description && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {p.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{chargerLabel(p)}</TableCell>
                <TableCell>
                  {p.tariff ? `${p.tariff.name} (${p.tariff.currency})` : "Charger default"}
                </TableCell>
                <TableCell>{eligibilityLabel(p)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      label={p.is_active ? "Active" : "Inactive"}
                      color={p.is_active ? "success" : "default"}
                      size="small"
                    />
                    {p.requires_code && (
                      <Chip label="Code required" icon={<VpnKeyIcon />} size="small" variant="outlined" color="warning" />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PlanDialog
        open={creating}
        plan={null}
        onClose={() => { setCreating(false); }}
        onSaved={() => { setCreating(false); load(); }}
      />
    </>
  );
}
